import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DocumentContentTypeSchema,
  DocumentOwnerTypeSchema,
  DocumentTypeSchema,
  SensitivityCategorySchema,
  type DocumentOwnerTypeDto,
  type DocumentTypeDto,
} from '@taqon/contracts';
import { z } from 'zod';
import { getData, getPage, patchJson, postJson } from '../../shared/api/client';
import { shortType } from '../../shared/documents/DocumentsPanel';
import { hasCapability, useSession } from '../../shared/session/session';
import { SearchFilterBar } from '../../shared/ui/filters';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';

const TYPES = '/api/v1/platform/document-types';
const PAGE_SIZE = 50;
const CONTENT_TYPES = DocumentContentTypeSchema.options;
const CATEGORIES = SensitivityCategorySchema.options;
const typesKey = ['platform', 'document-types'];

/**
 * Document types (`/admin/document-types`; FRD-CORE-023, SDD §17): which documents each kind of
 * record may carry, whether they track expiry or are confidential, and which file types they
 * accept. This is privileged configuration — every change is audited with its previous and new
 * values — and no type exists until an administrator configures it.
 */
export function DocumentTypesPage() {
  const session = useSession();
  const canCreate = hasCapability(session.data, 'platform.document_type:create');
  const canChange = hasCapability(session.data, 'platform.document_type:change');
  const [status, setStatus] = useState('');
  const [recordType, setRecordType] = useState('');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<DocumentTypeDto | null>(null);
  const [toggling, setToggling] = useState<DocumentTypeDto | null>(null);

  const ownerTypes = useQuery({
    queryKey: ['platform', 'document-owner-types'],
    queryFn: () =>
      getData('/api/v1/platform/document-owner-types', z.array(DocumentOwnerTypeSchema)),
  });
  const types = useQuery({
    queryKey: [...typesKey, status, recordType, page],
    queryFn: () =>
      getPage(
        `${TYPES}?pageSize=${String(PAGE_SIZE)}&page=${String(page)}&sort=applicableRecordType,name` +
          (status === '' ? '' : `&filter[status]=${status}`) +
          (recordType === ''
            ? ''
            : `&filter[applicable_record_type]=${encodeURIComponent(recordType)}`),
        DocumentTypeSchema,
      ),
  });
  const ownerName = (code: string) =>
    ownerTypes.data?.find((t) => t.code === code)?.displayName ?? code;
  const total = types.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Document types"
        description="The documents each kind of record may carry. Changes are audited."
        actions={
          canCreate ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={(event) => {
                event.currentTarget.focus();
                setCreating(true);
              }}
            >
              New document type
            </Button>
          ) : undefined
        }
      />
      <SearchFilterBar aria-label="Filters">
        <TextField
          select
          size="small"
          label="Record type"
          value={recordType}
          onChange={(e) => {
            setRecordType(e.target.value);
            setPage(1);
          }}
        >
          <MenuItem value="">All record types</MenuItem>
          {(ownerTypes.data ?? []).map((t) => (
            <MenuItem key={t.code} value={t.code}>
              {t.displayName}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <MenuItem value="">Any status</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </TextField>
      </SearchFilterBar>
      {types.isError && <ErrorState message={types.error.message} />}
      {types.isPending && <LoadingState label="Loading document types" />}
      {types.data !== undefined && (
        <Section title="Document types">
          {types.data.items.length === 0 ? (
            <EmptyState
              title="No document types"
              detail="Configure the documents each kind of record may carry."
            />
          ) : (
            <>
              <Table size="small" aria-label="Document types">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell>Record type</TableCell>
                    <TableCell>Controls</TableCell>
                    <TableCell>Accepted files</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {types.data.items.map((type) => (
                    <TableRow key={type.id}>
                      <TableCell sx={{ fontWeight: 600 }}>{type.name}</TableCell>
                      <TableCell>{type.code}</TableCell>
                      <TableCell>{ownerName(type.applicableRecordType)}</TableCell>
                      <TableCell>{controlsOf(type)}</TableCell>
                      <TableCell>{type.allowedContentTypes.map(shortType).join(', ')}</TableCell>
                      <TableCell>
                        <StatusChip status={type.status} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {canChange && (
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              aria-label={`Edit ${type.name}`}
                              onClick={(event) => {
                                event.currentTarget.focus();
                                setEditing(type);
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              color={type.status === 'active' ? 'warning' : 'primary'}
                              aria-label={`${type.status === 'active' ? 'Deactivate' : 'Activate'} ${type.name}`}
                              onClick={(event) => {
                                event.currentTarget.focus();
                                setToggling(type);
                              }}
                            >
                              {type.status === 'active' ? 'Deactivate' : 'Activate'}
                            </Button>
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                <Button
                  disabled={page <= 1}
                  onClick={() => {
                    setPage((p) => p - 1);
                  }}
                >
                  Previous
                </Button>
                <Typography variant="body2">
                  Page {page} of {lastPage} — {total} document types
                </Typography>
                <Button
                  disabled={page >= lastPage}
                  onClick={() => {
                    setPage((p) => p + 1);
                  }}
                >
                  Next
                </Button>
              </Box>
            </>
          )}
        </Section>
      )}
      {creating && (
        <DocumentTypeDialog
          ownerTypes={ownerTypes.data ?? []}
          existing={null}
          onClose={() => {
            setCreating(false);
          }}
        />
      )}
      {editing !== null && (
        <DocumentTypeDialog
          key={`${editing.id}:${String(editing.version)}`}
          ownerTypes={ownerTypes.data ?? []}
          existing={editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
      {toggling !== null && (
        <StatusDialog
          type={toggling}
          onClose={() => {
            setToggling(null);
          }}
        />
      )}
    </Stack>
  );
}

function controlsOf(type: DocumentTypeDto): string {
  const controls = [
    type.requiredForRecord && 'Required',
    type.expiryTracked && 'Expiry tracked',
    type.confidentialityCategory !== null &&
      `Confidential (${type.confidentialityCategory.replace(/_/g, ' ')})`,
    type.replacementAllowed && 'Replaceable',
    type.retentionClass !== null && `Retention: ${type.retentionClass}`,
  ].filter((x): x is string => typeof x === 'string');
  return controls.length === 0 ? '—' : controls.join(' · ');
}

/** Create a type, or change one: the code and record type are fixed once created. */
function DocumentTypeDialog({
  ownerTypes,
  existing,
  onClose,
}: {
  ownerTypes: readonly DocumentOwnerTypeDto[];
  existing: DocumentTypeDto | null;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [code, setCode] = useState(existing?.code ?? '');
  const [name, setName] = useState(existing?.name ?? '');
  const [recordType, setRecordType] = useState(existing?.applicableRecordType ?? '');
  const [required, setRequired] = useState(existing?.requiredForRecord ?? false);
  const [expiry, setExpiry] = useState(existing?.expiryTracked ?? false);
  const [replaceable, setReplaceable] = useState(existing?.replacementAllowed ?? false);
  const [category, setCategory] = useState<string>(existing?.confidentialityCategory ?? '');
  const [retention, setRetention] = useState(existing?.retentionClass ?? '');
  const [allowed, setAllowed] = useState<readonly string[]>(existing?.allowedContentTypes ?? []);
  const owner = ownerTypes.find((t) => t.code === recordType);

  const save = useMutation({
    mutationFn: () => {
      const flags = {
        name: name.trim(),
        requiredForRecord: required,
        expiryTracked: expiry,
        replacementAllowed: replaceable,
        allowedContentTypes: allowed,
      };
      if (existing === null) {
        return postJson(
          TYPES,
          {
            code: code.trim(),
            applicableRecordType: recordType,
            ...flags,
            ...(category !== '' ? { confidentialityCategory: category } : {}),
            ...(retention.trim() !== '' ? { retentionClass: retention.trim() } : {}),
          },
          DocumentTypeSchema,
          session.data?.csrfToken ?? '',
        );
      }
      return patchJson(
        `${TYPES}/${encodeURIComponent(existing.code)}`,
        {
          ...flags,
          confidentialityCategory: category === '' ? null : category,
          retentionClass: retention.trim() === '' ? null : retention.trim(),
        },
        DocumentTypeSchema,
        session.data?.csrfToken ?? '',
        existing.version,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: typesKey });
      await queryClient.invalidateQueries({ queryKey: ['document-types'] });
      onClose();
    },
  });

  return (
    <FormDialog
      open
      title={existing === null ? 'New document type' : `Edit ${existing.name}`}
      submitLabel={existing === null ? 'Create document type' : 'Save changes'}
      canSubmit={
        name.trim() !== '' &&
        allowed.length > 0 &&
        (existing !== null || (code.trim() !== '' && recordType !== ''))
      }
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <TextField
        label="Name"
        required
        value={name}
        onChange={(e) => {
          setName(e.target.value);
        }}
        slotProps={{ htmlInput: { maxLength: 100 } }}
      />
      <TextField
        label="Code"
        required
        disabled={existing !== null}
        value={code}
        onChange={(e) => {
          setCode(e.target.value.toLowerCase());
        }}
        helperText="A permanent identifier: lower case with underscores, for example trade_licence."
      />
      <TextField
        select
        label="Record type"
        required
        disabled={existing !== null}
        value={recordType}
        onChange={(e) => {
          setRecordType(e.target.value);
          setCategory('');
        }}
        helperText="The kind of record these documents attach to. It cannot be changed later."
      >
        {ownerTypes.map((t) => (
          <MenuItem key={t.code} value={t.code}>
            {t.displayName}
          </MenuItem>
        ))}
      </TextField>
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={required}
              onChange={(e) => {
                setRequired(e.target.checked);
              }}
            />
          }
          label="Required for the record"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={expiry}
              onChange={(e) => {
                setExpiry(e.target.checked);
              }}
            />
          }
          label="Expiry tracked — every document of this type carries an expiry date"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={replaceable}
              onChange={(e) => {
                setReplaceable(e.target.checked);
              }}
            />
          }
          label="Replacement allowed — a new version supersedes the old one, which is kept"
        />
      </FormGroup>
      <TextField
        select
        label="Confidentiality category"
        value={category}
        disabled={owner !== undefined && !owner.supportsConfidentiality}
        onChange={(e) => {
          setCategory(e.target.value);
        }}
        helperText="A confidential type is visible only to holders of the unmasked-access grant in this category on the record type."
      >
        <MenuItem value="">Not confidential</MenuItem>
        {CATEGORIES.map((value) => (
          <MenuItem key={value} value={value}>
            {value.replace(/_/g, ' ')}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Retention class"
        value={retention}
        onChange={(e) => {
          setRetention(e.target.value);
        }}
        slotProps={{ htmlInput: { maxLength: 50 } }}
        helperText="Informational only: retention periods are not yet approved, and no document is deleted."
      />
      <FormControl required error={allowed.length === 0} component="fieldset">
        <FormLabel component="legend">Accepted file types</FormLabel>
        <FormGroup row>
          {CONTENT_TYPES.map((contentType) => (
            <FormControlLabel
              key={contentType}
              control={
                <Checkbox
                  checked={allowed.includes(contentType)}
                  onChange={(e) => {
                    setAllowed((current) =>
                      e.target.checked
                        ? [...current, contentType]
                        : current.filter((t) => t !== contentType),
                    );
                  }}
                />
              }
              label={shortType(contentType)}
            />
          ))}
        </FormGroup>
        {allowed.length === 0 && <FormHelperText>Choose at least one file type.</FormHelperText>}
      </FormControl>
    </FormDialog>
  );
}

/** Activating or deactivating is consequential: an inactive type accepts no new uploads. */
function StatusDialog({ type, onClose }: { type: DocumentTypeDto; onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const deactivating = type.status === 'active';
  const change = useMutation({
    mutationFn: () =>
      patchJson(
        `${TYPES}/${encodeURIComponent(type.code)}`,
        { status: deactivating ? 'inactive' : 'active' },
        DocumentTypeSchema,
        session.data?.csrfToken ?? '',
        type.version,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: typesKey });
      await queryClient.invalidateQueries({ queryKey: ['document-types'] });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      title={`${deactivating ? 'Deactivate' : 'Activate'} ${type.name}?`}
      submitLabel={deactivating ? 'Deactivate' : 'Activate'}
      canSubmit
      submitting={change.isPending}
      error={change.isError ? change.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        change.mutate();
      }}
    >
      <Alert severity={deactivating ? 'warning' : 'info'} variant="outlined">
        {deactivating
          ? 'No new documents of this type can be uploaded or replaced. Existing documents stay available.'
          : 'Documents of this type can be uploaded again.'}
      </Alert>
    </FormDialog>
  );
}
